import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { cn } from '@shared/lib';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HStack,
  InfoHint,
  Label,
  PopoverSelect,
  RequiredMark,
  Text,
} from '@shared/ui';
import { faqErrorKey, useFaqUpdate } from '../model/use-faq-update';

export function FaqUpdateDialog({
  scope,
  onClose,
  onSaved,
}: {
  scope: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const opener = useRef(
    document.activeElement instanceof HTMLElement ? document.activeElement : null
  );
  const flow = useFaqUpdate(scope, onSaved);
  const feedback = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (flow.error) feedback.current?.focus();
  }, [flow.error]);
  const locked = Boolean(flow.preview || flow.pending || flow.recover === 'save');
  const modulesLoading = flow.modules.isFetching;
  const modulesError = Boolean(flow.modules.error) && !modulesLoading;
  const dateFormatter = new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: 'medium' });
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        closeLabel={t('common.close')}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          opener.current?.focus();
        }}
        className={cn(
          'max-h-[90dvh] w-[calc(100%-2rem)] overflow-y-auto p-4 sm:p-6',
          flow.preview ? 'max-w-5xl' : 'max-w-2xl'
        )}
      >
        <DialogHeader>
          <HStack gap="sm" align="center">
            <DialogTitle>{t('faq.title')}</DialogTitle>
            <InfoHint className="shrink-0" text={t('faq.description')} />
          </HStack>
          <DialogDescription className="sr-only">{t('faq.description')}</DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-2">
          <Label htmlFor="faq-module">
            {t('faq.module')}
            <RequiredMark />
          </Label>
          <PopoverSelect
            id="faq-module"
            triggerAriaLabel={t('faq.module')}
            value={flow.moduleId}
            onValueChange={flow.select}
            onOpenChange={(open) => {
              if (open && modulesError) void flow.modules.refetch();
            }}
            options={(flow.modules.data ?? []).map((item) => ({
              value: item.id,
              label: item.title ?? t('crm.missingValue'),
              description: item.faq.updatedAt
                ? t('faq.updatedAt', {
                    date: dateFormatter.format(new Date(item.faq.updatedAt)),
                  })
                : undefined,
            }))}
            placeholder={t('faq.chooseModule')}
            emptyText={modulesError ? '' : t('faq.empty')}
            loading={modulesLoading}
            loadingLabel={t('common.loading')}
            disabled={locked}
            required
            aria-invalid={modulesError || undefined}
            aria-describedby={modulesError ? 'faq-module-error' : undefined}
          />
          {modulesError ? (
            <Text id="faq-module-error" role="alert" size="sm" color="destructive">
              {t('faq.errors.modules')}
            </Text>
          ) : null}
        </div>
        {flow.preview ? (
          <section className="min-w-0 space-y-4" aria-label={t('faq.preview')}>
            <Text color="muted" size="sm">
              {t('faq.review', { count: flow.preview.output.pairs.length })}
            </Text>
            <ol className="list-decimal space-y-4 pl-6">
              {flow.preview.output.pairs.map((pair, index) => (
                <li key={index} className="space-y-2 break-words">
                  <Text className="whitespace-pre-wrap font-semibold">{pair.question}</Text>
                  <Text className="whitespace-pre-wrap">{pair.answer}</Text>
                </li>
              ))}
            </ol>
          </section>
        ) : null}
        {flow.error ? (
          <Alert ref={feedback} tabIndex={-1} variant="destructive">
            <AlertDescription>{t(faqErrorKey(flow.error, Boolean(flow.preview)))}</AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter className="flex-row flex-wrap justify-end gap-2 sm:space-x-0">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {flow.preview ? (
            <>
              <Button
                variant="outline"
                disabled={Boolean(flow.pending || flow.recover === 'save')}
                onClick={() => {
                  flow.select(flow.moduleId);
                }}
              >
                {t('faq.changeModule')}
              </Button>
              <Button
                variant="outline"
                disabled={Boolean(flow.pending || flow.recover === 'save')}
                onClick={() => {
                  void flow.generate();
                }}
              >
                <Sparkles aria-hidden="true" />
                {t('faq.regenerate')}
              </Button>
              <Button
                loading={flow.pending === 'save'}
                aria-label={flow.pending === 'save' ? t('faq.saving') : undefined}
                disabled={Boolean(flow.pending)}
                onClick={() => {
                  void flow.save();
                }}
              >
                {t(flow.recover === 'save' ? 'faq.recoverSave' : 'common.save')}
              </Button>
            </>
          ) : (
            <Button
              loading={flow.pending === 'generate'}
              disabled={
                !flow.selected || modulesLoading || Boolean(flow.pending || flow.modules.error)
              }
              onClick={() => {
                void flow.generate();
              }}
            >
              {flow.pending !== 'generate' ? <Sparkles aria-hidden="true" /> : null}
              {t(
                flow.pending === 'generate'
                  ? 'faq.generating'
                  : flow.recover === 'generate'
                    ? 'faq.recoverPreview'
                    : 'faq.generate'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
